import { Show, For, createMemo, createSignal, createEffect, onCleanup } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { qty, relTime } from '../lib/format.js';
import { tipProps } from '../components/MouseTooltip.jsx';
import ThemedTable, { RelTimeCell } from '../components/ThemedTable/index.js';
import wm from '../lib/wmStore.js';
import './SyncPoints.css';

/**
 * Your Sync Data: sync-point summary, 8-week trend, and searchable recent
 * sync sessions in one window (#391). Modernized with ThemedTable.
 */
export default function SyncPoints(props) {
  const [q, setQ] = createSignal(props.settings?.q || props.settings?.search || '');
  const [qDebounced, setQDebounced] = createSignal(q());
  let qTimer = null;
  createEffect(() => {
    const val = q();
    clearTimeout(qTimer);
    qTimer = setTimeout(() => {
      setQDebounced(val);
      const wid = props.widget?.id;
      if (wid && (props.widget?.settings?.q || '') !== val) {
        wm.setSettings(wid, { ...(props.widget?.settings || {}), q: val });
      }
    }, 220);
  });
  onCleanup(() => clearTimeout(qTimer));

  const params = createMemo(() => {
    const limit = Math.max(1, Math.min(parseInt(props.settings?.limit, 10) || 50, 100));
    const search = qDebounced().trim();
    return search ? { limit, q: search } : { limit };
  });

  const { data, loading, error, refetch } = useWidgetData('sync-points', params);
  const history = createMemo(() => (data()?.history || []).slice().reverse());
  const max = createMemo(() => Math.max(1, ...history().map(h => h.points || 0)));
  const lastWeek = createMemo(() => (data()?.history || []).find(h => h.weekOffset === 1)?.points || 0);
  const cw = createMemo(() => data()?.currentWeek || { points: 0, sales: 0 });
  const delta = createMemo(() => {
    const a = cw().points, b = lastWeek();
    if (!a || !b) return null;
    return Math.round(((a - b) / b) * 100);
  });
  const sessions = createMemo(() => data()?.sessions || []);
  const sessionsTotal = createMemo(() => data()?.sessionsTotal || sessions().length || 0);
  const sessionsErr = createMemo(() => data()?.sessionsError || null);

  function pickSession(id) {
    if (!id) return;
    window.dispatchEvent(new CustomEvent('v2-sync-session-pick', { detail: { sessionId: id } }));
  }

  function trendLabel(h) {
    return h.weekOffset === 0 ? 'this week' : `${h.weekOffset} week${h.weekOffset === 1 ? '' : 's'} ago`;
  }

  function syncPts(value) {
    return `${qty(value || 0)} pts`;
  }

  const columns = [
    {
      key: 'startedAt',
      label: 'When',
      sortable: true,
      type: 'time',
      canHide: false,
      getValue: s => s.startedAt || 0,
      render: (v) => <RelTimeCell ts={v} />,
    },
    {
      key: 'syncType',
      label: 'Type',
      sortable: true,
      type: 'string',
      align: 'center',
      getValue: s => s.syncType || 'mm',
      render: (v) => (
        <span class="v2-tt-meta-badge" style={{ 'font-weight': '600' }}>
          {(v || 'mm').toUpperCase()}
        </span>
      ),
    },
    {
      key: 'itemsAdded',
      label: 'Items',
      sortable: true,
      type: 'number',
      align: 'right',
      getValue: s => s.itemsAdded || 0,
      render: (v) => <span class="v2-tt-mono">{qty(v)}</span>,
    },
    {
      key: 'salesAdded',
      label: 'Sales',
      sortable: true,
      type: 'number',
      align: 'right',
      getValue: s => s.salesAdded || 0,
      render: (v) => <span class="v2-tt-mono">{qty(v)}</span>,
    },
    {
      key: 'points',
      label: 'Points',
      sortable: true,
      type: 'number',
      align: 'right',
      getValue: s => s.points || 0,
      render: (v) => <strong class="v2-tt-gold">{qty(v)} pts</strong>,
    },
    {
      key: 'detail',
      label: '',
      sortable: false,
      align: 'center',
      width: '28px',
      render: () => <span style={{ color: 'var(--cyan, #38bdf8)', 'font-size': '13px' }}>›</span>,
    },
  ];

  return (
    <div class="v2-widget v2-sync-data">
      <div class="v2-widget-hdr v2-sync-data__toolbar">
        <input
          type="text"
          class="v2-input v2-sync-data__search"
          placeholder="Search guild / type / notes..."
          value={q()}
          onInput={(e) => setQ(e.currentTarget.value)}
          {...tipProps({ content: 'Search recent sync sessions by guild, sync type, or operator notes.', pin: false })}
        />
        <button
          type="button"
          class="v2-widget-hdr__btn"
          {...tipProps({ content: 'Refresh your points and recent sync-session history.', pin: false })}
          onClick={() => refetch()}
        >↻</button>
      </div>

      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-sync-data__hero">
            <div class="v2-stat v2-sync-card v2-sync-card--rank" {...tipProps({ content: `Your all-time sync-point rank among ${qty(data()?.total || 0)} ranked syncers.`, pin: false })}>
              <span class="v2-stat__label">Rank</span>
              <span class="v2-stat__value">{data()?.rank ? `#${data().rank}` : '—'}</span>
              <span class="v2-stat__sub">of {qty(data()?.total || 0)}</span>
            </div>
            <div class="v2-stat v2-sync-card v2-sync-card--gold" {...tipProps({ content: 'All-time sync points from the current leaderboard snapshot.', pin: false })}>
              <span class="v2-stat__label">All-time</span>
              <span class="v2-stat__value">{syncPts(data()?.points)}</span>
            </div>
            <div class="v2-stat v2-sync-card v2-sync-card--live" {...tipProps({ content: 'Live current-week points and counted sales from your synced trade data.', pin: false })}>
              <span class="v2-stat__label">This week</span>
              <span class="v2-stat__value">{syncPts(cw().points)}</span>
              <span class="v2-stat__sub">
                {qty(cw().sales)} sales
                <Show when={delta() != null}>
                  {' · '}
                  <span style={{ color: delta() >= 0 ? 'var(--t-green, #4ade80)' : 'var(--bad, #f87171)' }}>
                    {delta() >= 0 ? '▲' : '▼'} {Math.abs(delta())}%
                  </span>
                </Show>
              </span>
            </div>
          </div>

          <div class="v2-sync-data__trend">
            <div class="v2-sync-data__section-title">
              <span>Last 8 weeks</span>
              <span>{qty(history().reduce((sum, h) => sum + (h.points || 0), 0))} pts</span>
            </div>
            <div class="v2-sync-data__bars">
              <For each={history()}>{(h) => (
                <div
                  class="v2-sync-data__bar"
                  classList={{ 'is-live': h.weekOffset === 0, 'is-empty': !h.points }}
                  {...tipProps({ content: `${trendLabel(h)}: ${syncPts(h.points)}${h.isLive ? '\nlive calculation' : '\nlocked snapshot'}`, pin: false })}
                  style={{
                    height: `${Math.max(3, (h.points / max()) * 70)}px`,
                  }} />
              )}</For>
            </div>
          </div>

          <div class="v2-sync-data__history" style={{ flex: 1, 'min-height': 0, display: 'flex', 'flex-direction': 'column' }}>
            <div class="v2-sync-data__section-title">
              <span>Recent syncs</span>
              <span>{qty(sessions().length)} of {qty(sessionsTotal())}</span>
            </div>

            <Show when={sessionsErr()}>
              <div class="v2-error" style={{ 'font-size': '11px', 'margin-bottom': '6px' }}>{sessionsErr()}</div>
            </Show>

            <div style={{ flex: 1, 'min-height': 0 }}>
              <ThemedTable
                columns={columns}
                rows={sessions()}
                storageKey="sync_points_sessions"
                defaultSortKey="startedAt"
                defaultSortDir="desc"
                searchPlaceholder="Filter sync history…"
                emptyText={data()?.sessionsNote || 'No recorded sync sessions yet.'}
                onRowClick={(row) => pickSession(row.id)}
                compact={true}
              />
            </div>
          </div>
        </Show>
      </Show>
    </div>
  );
}
